import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassHeroContainer,
} from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const TermsOfServicePage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <TermsContainer>
          {/* Header Section */}
          <HeaderSection>
            <HeadingWrapper>
              <MainHeading>Word of Mouth Ltd</MainHeading>
              <SubHeaderRow>
                <SubHeading>Terms of Service</SubHeading>
                <LastUpdated>Last updated: 04 June 2025</LastUpdated>
              </SubHeaderRow>
            </HeadingWrapper>
          </HeaderSection>

          {/* Section 1: Introduction */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>1. Introduction</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Welcome to Word of Mouth ("Platform", "we", "us", or "our").
                <br />
                Word of Mouth Ltd is a UK company (Company number 16267512 , registered in England and Wales), providing an online platform where clients ("Clients") can connect with local service providers ("Providers") for various services, including but not limited to handyman services, cleaning, personal care, and other home and lifestyle services.
                <br />
                By accessing or using our Platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Platform.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 2: Use of the Platform */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>2. Use of the Platform</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Our Platform serves as a marketplace that facilitates connections between Clients and Providers.
                <br />
                Word of Mouth Ltd is not a direct provider of services — services are performed by independent Providers.
                <br />
                You agree to use the Platform in compliance with all applicable laws and regulations in the United Kingdom.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 3: Account Registration and Verification */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>3. Account Registration and Verification</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                To access certain features of the Platform, including posting or accepting service requests, both Clients and Providers must create an account.
                <br />
                Providers are required to undergo identity verification.
                <br />
                We use Persona, a third-party service, to perform identity checks, including ID document and biometric verification (selfie).
                <br />
                By registering, you consent to this verification process.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 4: Fees and Payments */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>4. Fees and Payments</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Fees for services are agreed directly between Clients and Providers via the Platform.
                <br />
                Word of Mouth Ltd may charge service fees or platform commissions, which will be clearly disclosed to users.
                <br />
                Payments are processed through trusted third-party payment providers.
                <br />
                Word of Mouth Ltd does not directly handle or store payment card information.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 5: User Responsibilities */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>5. User Responsibilities</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                You agree to provide accurate and complete information when registering on the Platform.
                <br />
                You are responsible for all activity that occurs under your account.
                <br />
                You must not use the Platform for any unlawful or fraudulent purpose.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6: Limitation of Liability */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6. Limitation of Liability</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Word of Mouth Ltd is not responsible for the performance, quality, or outcome of services provided by Providers.
                <br />
                We do not guarantee the suitability, conduct, or qualifications of any Provider.
                <br />
                To the maximum extent permitted by law, Word of Mouth Ltd shall not be liable for any indirect or consequential damages arising from use of the Platform.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 7: Privacy */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>7. Privacy</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Your privacy is important to us.
                <br />
                Please refer to our Privacy Policy for information about how we collect, use, and protect your personal data.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 8: Modifications */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>8. Modifications</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We may modify these Terms from time to time.
                <br />
                Updated Terms will be posted on the Platform. Continued use of the Platform after changes constitutes acceptance of the new Terms.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 9: Contact Us */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>9. Contact Us</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                If you have any questions about these Terms, please contact us at:
                <br />
                Word of Mouth Ltd
                <br />
                Email: support@womapp.uk
                <br />
                Registered office: Cambridge House, Highgate, Rorest Row, East Sussex, United Kingdom RH18 5BA
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>
        </TermsContainer>

        {/* Get Started CTA */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 8, mb: 6 }}>
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Main container - Frame 8
const TermsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '24px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '0 20px',
  },
}));

// Frame 4 - Header Section
const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '48px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Frame 5 - Heading Wrapper
const HeadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Main Heading - Word of Mouth Ltd
const MainHeading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '36px',
  },
}));

// Frame 6 - Sub Header Row
const SubHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
}));

// Sub Heading - Terms of Service
const SubHeading = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '133.4%',
  textAlign: 'center',
  color: theme.palette.mode === 'dark' ? '#E5E5E5' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

// Last Updated
const LastUpdated = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  textAlign: 'center',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

// Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title Wrapper
const SectionTitleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title
const SectionTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',
  textAlign: 'left',
}));

// Section Content Wrapper
const SectionContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',
}));

export default TermsOfServicePage;
